import { MouseEvent } from "react";
import { MdClose, MdEdit } from "react-icons/md";
import { FormattedDate, FormattedMessage, useIntl } from "react-intl";
import {
  ConnectionHandler,
  graphql,
  useMutation,
  usePaginationFragment,
} from "react-relay";
import { Link } from "react-router";
import { toast } from "sonner";
import { InferOutput, object, string } from "valibot";
import {
  ResponsiveModal,
  ResponsiveModalCloseDrawer,
  ResponsiveModalContent,
  ResponsiveModalDescription,
  ResponsiveModalTitle,
  ResponsiveModalTrigger,
} from "@/kit/ui/responsive-modal";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/kit/ui/form";
import { useValibotForm } from "@/utils/validation";
import Button, { ToolbarButton } from "./Button";
import EntityProfilePic from "./EntityProfilePic";
import LoadMore from "./LoadMore";
import { LazyEntitiesAutoCompleteSelection } from "./LazyEntitiesAutoComplete";
import { CompetitionJuryAddMutation } from "./__generated__/CompetitionJuryAddMutation.graphql";
import { CompetitionJuryFragment$key } from "./__generated__/CompetitionJuryFragment.graphql";
import { CompetitionJuryRemoveMutation } from "./__generated__/CompetitionJuryRemoveMutation.graphql";

const Fragment = graphql`
  fragment CompetitionJuryFragment on Competition
  @refetchable(queryName: "CompetitionJuryFragmentPaginationQuery")
  @argumentDefinitions(
    cursor: { type: "String" }
    count: { type: "Int", defaultValue: 10 }
  ) {
    viewerCanAddJuryMember: viewerCan(action: ADD_COMPETITION_JURY)
    viewerCanRemoveJuryMember: viewerCan(action: REMOVE_COMPETITION_JURY)
    jury(first: $count, after: $cursor)
      @connection(key: "CompetitionJuryFragment_jury") {
      edges {
        cursor
        selectedOn
        node {
          id
          username
          displayName
          ... on User {
            jobTitle
          }
          ...EntityProfilePicFragment @arguments(thumbnail: true)
        }
      }
    }
  }
`;

const AddMutation = graphql`
  mutation CompetitionJuryAddMutation(
    $competitionId: ID!
    $entityId: ID!
    $connections: [ID!]!
  ) {
    addCompetitionJury(competitionId: $competitionId, entityId: $entityId)
      @appendEdge(connections: $connections) {
      cursor
      selectedOn
      node {
        id
        username
        displayName
        ... on User {
          jobTitle
        }
        ...EntityProfilePicFragment @arguments(thumbnail: true)
      }
    }
  }
`;

const RemoveMutation = graphql`
  mutation CompetitionJuryRemoveMutation(
    $competitionId: ID!
    $entityId: ID!
    $connections: [ID!]!
  ) {
    removeCompetitionJury(competitionId: $competitionId, entityId: $entityId) {
      id @deleteEdge(connections: $connections)
      displayName
    }
  }
`;

export interface CompetitionJuryProps {
  competition: CompetitionJuryFragment$key;
}

export default function CompetitionJury(props: CompetitionJuryProps) {
  const {
    data: { jury, viewerCanAddJuryMember, viewerCanRemoveJuryMember },
  } = usePaginationFragment(Fragment, props.competition);

  if (!(jury.edges instanceof Array)) {
    return null;
  }

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold w-full font-poppins">
        <FormattedMessage defaultMessage="Jury" />
      </h1>
      <div className="flex gap-4 items-center">
        {jury.edges.map(({ node }) => (
          <Link
            to={`/${node.username}`}
            className="flex flex-col gap-1 items-center relative px-2"
            key={node.id}
          >
            <EntityProfilePic size="10" entity={node} />
            <span>{node.displayName}</span>
          </Link>
        ))}

        {jury.edges.length <= 0 && (
          <span className="italic">
            <FormattedMessage defaultMessage="A jury is expected to be selected shortly." />
          </span>
        )}

        {(viewerCanAddJuryMember || viewerCanRemoveJuryMember) && (
          <CompetitionJuryEditButton competition={props.competition} />
        )}
      </div>
    </div>
  );
}

interface CompetitionJuryEditButtonProps {
  competition: CompetitionJuryFragment$key;
}

const formSchema = object({ entityId: string() });
type FormValues = InferOutput<typeof formSchema>;

function CompetitionJuryEditButton(props: CompetitionJuryEditButtonProps) {
  const intl = useIntl();
  const {
    data: {
      viewerCanAddJuryMember,
      viewerCanRemoveJuryMember,
      jury,
      ...competition
    },
    hasNext,
    isLoadingNext,
    loadNext,
  } = usePaginationFragment(Fragment, props.competition);

  const form = useValibotForm(formSchema);
  const [commitAddMutation, isAddMutationInFlight] =
    useMutation<CompetitionJuryAddMutation>(AddMutation);
  const [commitRemoveMutation, isRemoveMutationInFlight] =
    useMutation<CompetitionJuryRemoveMutation>(RemoveMutation);

  const onAddJuryMember = ({ entityId }: FormValues) => {
    commitAddMutation({
      variables: {
        competitionId: competition.id,
        entityId,
        connections: [
          ConnectionHandler.getConnectionID(
            competition.id,
            "CompetitionJuryFragment_jury",
          ),
        ],
      },
      onCompleted: ({ addCompetitionJury: { node } }) => {
        form.reset();
        toast.success(
          <FormattedMessage
            defaultMessage="Elected {user} as jury member successfully!"
            values={{ user: node.displayName }}
          />,
        );
      },
      onError: (error) => {
        console.error(error);
        toast.error(
          <FormattedMessage defaultMessage="Cannot elect jury member at the moment, please retry later" />,
        );
      },
    });
  };

  const onRemoveJuryMember = (entityId: ID, _evt: MouseEvent) => {
    commitRemoveMutation({
      variables: {
        entityId,
        competitionId: competition.id,
        connections: [
          ConnectionHandler.getConnectionID(
            competition.id,
            "CompetitionJuryFragment_jury",
          ),
        ],
      },
      onCompleted: ({ removeCompetitionJury: node }) => {
        toast.success(
          <FormattedMessage
            defaultMessage="{user} successfully removed from jury!"
            values={{ user: node.displayName }}
          />,
        );
      },
      onError: (error) => {
        console.error(error);
        toast.error(
          <FormattedMessage defaultMessage="Cannot remove jury member at the moment, please retry later" />,
        );
      },
    });
  };

  return (
    <ResponsiveModal>
      <ResponsiveModalTrigger asChild>
        <Button
          kind="text"
          title={intl.formatMessage({ defaultMessage: "Edit jury" })}
          className="text-lg flex gap-2 items-center"
        >
          {jury.edges.length <= 0 && (
            <FormattedMessage defaultMessage="Edit jury" />
          )}
          <MdEdit />
        </Button>
      </ResponsiveModalTrigger>

      <ResponsiveModalContent
        className="md:min-w-[67%] xl:min-w-[50%]"
        onOpenAutoFocus={(event) => event.preventDefault()}
      >
        <ResponsiveModalTitle className="flex-1">
          <FormattedMessage defaultMessage="Edit jury" />
        </ResponsiveModalTitle>
        <ResponsiveModalDescription>
          <FormattedMessage defaultMessage="Search and add a user a juror." />
        </ResponsiveModalDescription>

        <div className="flex flex-col w-full px-4 md:py-2 py-4 gap-4">
          {viewerCanAddJuryMember && (
            <Form form={form} onSubmit={onAddJuryMember}>
              <div className="flex flex-row gap-3">
                <FormField
                  control={form.control}
                  name="entityId"
                  render={({ field }) => (
                    <FormItem className="w-full">
                      <FormControl>
                        <LazyEntitiesAutoCompleteSelection
                          kind="USER"
                          placeholder={intl.formatMessage({
                            defaultMessage: "username",
                          })}
                          {...field}
                        />
                      </FormControl>

                      <FormMessage />
                    </FormItem>
                  )}
                />
                <Button
                  type="submit"
                  kind="primary"
                  disabled={isAddMutationInFlight}
                >
                  <FormattedMessage defaultMessage="Elect" />
                </Button>
              </div>
            </Form>
          )}

          <table>
            <thead>
              <tr className="*:px-2 *:py-3 *:whitespace-nowrap *:bg-gray-100 *:font-bold">
                <th>
                  <FormattedMessage defaultMessage="User" />
                </th>
                <th>
                  <FormattedMessage defaultMessage="Selected on" />
                </th>
                <th></th>
              </tr>
            </thead>

            <tbody>
              {jury.edges.map(({ node, selectedOn }) => (
                <tr key={node.id} className="*:px-2 *:py-3 *:whitespace-nowrap">
                  <td>
                    <div className="flex gap-2 items-center">
                      <EntityProfilePic entity={node} size="10" />
                      <div>
                        <span>{node.displayName}</span>
                        <br />
                        <span className="text-sm">{node.jobTitle}</span>
                      </div>
                    </div>
                  </td>
                  <td>
                    <FormattedDate value={selectedOn} />
                  </td>
                  <td className="w-[0.01%]">
                    {viewerCanRemoveJuryMember && (
                      <ToolbarButton
                        disabled={isRemoveMutationInFlight}
                        onClick={onRemoveJuryMember.bind(null, node.id)}
                        title={intl.formatMessage({
                          defaultMessage: "Remove jury member",
                        })}
                      >
                        <MdClose className="text-lg" />
                      </ToolbarButton>
                    )}
                  </td>
                </tr>
              ))}

              {jury.edges.length <= 0 && (
                <tr>
                  <td colSpan={3} className="px-2 py-3 text-center italic">
                    <FormattedMessage defaultMessage="No jury has been elected yet." />
                  </td>
                </tr>
              )}
            </tbody>

            {hasNext && (
              <tfoot>
                <tr>
                  <td colSpan={3}>
                    <LoadMore
                      loadMore={loadNext}
                      isLoading={isLoadingNext}
                      hasMore
                    />
                  </td>
                </tr>
              </tfoot>
            )}
          </table>

          <ResponsiveModalCloseDrawer className="mt-8 w-full" asChild>
            <Button kind="secondary" className="w-full">
              <FormattedMessage defaultMessage="Close" />
            </Button>
          </ResponsiveModalCloseDrawer>
        </div>
      </ResponsiveModalContent>
    </ResponsiveModal>
  );
}
